import React, { useState, useEffect } from 'react';
import { useLocation, Navigate } from 'react-router-dom';
import '../assets/css/category-videos.css';
import '../assets/css/video-showcase.css';
import HeaderOne from '../Components/Header/HeaderOne';
import FooterOne from '../Components/Footer/FooterOne';
import ScrollToTop from '../Components/ScrollToTop';

const API_BASE = process.env.REACT_APP_API_URL || '';

function buildUrl(path) {
  return `${API_BASE.replace(/\/$/, '')}${path}`;
}

const CATEGORIES = {
  'travel-with-dinesh': { name: 'Travel with dinesh', subtitle: 'දිනේෂ් සමඟ සංචාරය' },
  'ape-pansala': { name: 'Ape pansala', subtitle: 'අපෙ පන්සල' },
  'urumaya': { name: 'Urumaya', subtitle: 'උරුමය' },
  'ranga-rata': { name: 'Ranga Rata', subtitle: 'රංග රටා' },
};

const VALID_SLUGS = Object.keys(CATEGORIES);

function CategoryVideosPage() {
  const { pathname } = useLocation();
  const categorySlug = pathname.replace(/^\//, '').split('/')[0];
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);

  const category = categorySlug && CATEGORIES[categorySlug];

  useEffect(() => {
    if (!categorySlug || !VALID_SLUGS.includes(categorySlug)) return;
    let cancelled = false;
    setLoading(true);
    fetch(buildUrl(`/api/videos?category=${encodeURIComponent(categorySlug)}`))
      .then((res) => res.json())
      .then((data) => {
        if (!cancelled && data.success) setVideos(data.videos || []);
      })
      .catch(() => {
        if (!cancelled) setVideos([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [categorySlug]);

  if (!category) {
    return <Navigate to="/" replace />;
  }

  const getVideoSrc = (url) => {
    if (!url) return '';
    if (typeof window !== 'undefined' && url.startsWith('/uploads/videos/')) {
      return window.location.origin + url;
    }
    return url;
  };

  const imageSrc = (path) => {
    if (!path) return '';
    const p = String(path).trim();
    if (typeof window !== 'undefined' && p.startsWith('/uploads/')) {
      return window.location.origin + (p.startsWith('/') ? p : '/' + p);
    }
    if (typeof window !== 'undefined' && p.startsWith('/')) {
      return window.location.origin + p;
    }
    return p;
  };

  const openModal = (video) => {
    setActive(video);
  };

  const closeModal = () => {
    setActive(null);
  };

  const renderModalContent = () => {
    if (!active) return null;
    const src = getVideoSrc(active.url);
    const isUploaded = (active.url || '').startsWith('/uploads/videos/');
    const isYouTube = /youtube\.com|youtu\.be/i.test(active.url || '');

    if (isUploaded) {
      return (
        <video
          src={src}
          controls
          autoPlay
          preload="metadata"
          playsInline
          className="video-showcase-modal-player"
        >
          Your browser does not support the video tag.
        </video>
      );
    }

    if (isYouTube) {
      let embedUrl = active.url;
      try {
        const u = new URL(active.url);
        if (u.hostname.includes('youtu.be')) {
          const id = u.pathname.replace('/', '');
          embedUrl = `https://www.youtube.com/embed/${id}`;
        } else if (u.searchParams.get('v')) {
          embedUrl = `https://www.youtube.com/embed/${u.searchParams.get('v')}`;
        }
      } catch (_) {
        // fall back to original url
      }
      return (
        <iframe
          src={embedUrl}
          title={active.title || 'Video'}
          className="video-showcase-modal-player"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      );
    }

    return (
      <video
        src={src}
        controls
        autoPlay
        preload="metadata"
        playsInline
        className="video-showcase-modal-player"
      >
        Your browser does not support the video tag.
      </video>
    );
  };

  return (
    <>
      <HeaderOne />
      <ScrollToTop />

      <style>{`
        /* Light theme: remove dark colors, make backgrounds white and text black */
        .video-showcase-hero{
          /* Creative two-color mix (light cyan + light violet) */
          background:
            radial-gradient(circle at 15% 15%, rgba(56,189,248,0.65) 0%, rgba(56,189,248,0) 45%),
            radial-gradient(circle at 85% 35%, rgba(167,139,250,0.55) 0%, rgba(167,139,250,0) 50%),
            linear-gradient(180deg, rgba(255,255,255,1) 0%, rgba(248,250,252,1) 100%) !important;
          border-bottom: 1px solid rgba(0,0,0,0.08) !important;
          color: #111827 !important;
        }

        .video-showcase-grid-section{
          background: #ffffff !important;
          color: #111827 !important;
          border-top: 1px solid rgba(0,0,0,0.06) !important;
        }

        .video-showcase-title,
        .video-showcase-hero .video-showcase-title{
          color: #111827 !important;
        }

        .video-showcase-loading{
          color: #111827 !important;
        }

        .video-showcase-tagline{
          color: #111827 !important;
          border-color: rgba(0,0,0,0.12) !important;
          background: rgba(255,255,255,0.9) !important;
          text-shadow: none !important;
        }

        .video-showcase-subtitle,
        .video-showcase-hero .video-showcase-subtitle{
          color: #111827 !important;
          text-shadow: none !important;
        }

        .video-showcase-card{
          background: #ffffff !important;
          border: 1px solid rgba(0,0,0,0.12) !important;
          box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08) !important;
        }

        .video-showcase-card:hover{
          background: #f8fafc !important;
          border-color: rgba(0,0,0,0.22) !important;
          box-shadow: 0 18px 45px rgba(15, 23, 42, 0.12) !important;
        }

        .video-showcase-thumb{
          background: #ffffff !important;
        }

        .video-showcase-thumb--placeholder{
          background: #ffffff !important;
          color: #111827 !important;
        }

        .video-showcase-thumb-label{
          border-color: rgba(0,0,0,0.15) !important;
          background: rgba(255,255,255,0.95) !important;
          color: #111827 !important;
        }

        .video-showcase-play-overlay{
          background: radial-gradient(circle at center, rgba(15, 23, 42, 0.06), rgba(15, 23, 42, 0.2)) !important;
        }

        .video-showcase-play-icon{
          background: #111827 !important;
          box-shadow: 0 12px 28px rgba(0,0,0,0.18) !important;
          color: #ffffff !important;
        }

        .video-showcase-card-title{
          color: #111827 !important;
        }

        .video-showcase-modal-overlay{
          background: rgba(255,255,255,0.88) !important;
          backdrop-filter: blur(10px) !important;
        }

        .video-showcase-modal{
          background: #ffffff !important;
          border-color: rgba(0,0,0,0.15) !important;
          box-shadow: 0 40px 120px rgba(0,0,0,0.18) !important;
        }

        .video-showcase-modal-close{
          background: #ffffff !important;
          border-color: rgba(0,0,0,0.15) !important;
          color: #111827 !important;
        }

        /* (Kept) Make category-page text brighter (Travel with dinesh / Ape pansala / Urumaya / Ranga Rata) */
        .video-showcase-hero .video-showcase-tagline{
          color: #111827 !important;
          border-color: rgba(0,0,0,0.12) !important;
          background: rgba(255,255,255,0.92) !important;
          text-shadow: none !important;
        }

        .video-showcase-hero .video-showcase-subtitle{
          color: #111827 !important;
          text-shadow: none !important;
        }
      `}</style>

      <section className="video-showcase-hero">
        <div className="container th-container">
          <div className="video-showcase-hero-inner">
            <span className="video-showcase-tagline">{category.subtitle}</span>
            <h1 className="video-showcase-title">Video Showcase</h1>
            <p className="video-showcase-subtitle">{category.name}</p>
          </div>
        </div>
      </section>

      <section className="video-showcase-grid-section">
        <div className="container th-container">
          {loading ? (
            <div className="video-showcase-loading">
              <p>Loading videos…</p>
            </div>
          ) : videos.length === 0 ? (
            <div className="video-showcase-loading">
              <p>No videos in this category yet.</p>
            </div>
          ) : (
            <div className="video-showcase-grid">
              {videos.map((video) => {
                const src = getVideoSrc(video.url);
                const isUploaded = (video.url || '').startsWith('/uploads/videos/');
                const thumbSrc = imageSrc(video.thumbnail || '');
                return (
                  <button
                    type="button"
                    key={video._id}
                    className="video-showcase-card"
                    onClick={() => openModal(video)}
                  >
                    <div className="video-showcase-thumb-wrap">
                      {isUploaded && thumbSrc ? (
                        <img
                          src={thumbSrc}
                          alt={video.title || 'Video'}
                          className="video-showcase-thumb"
                          style={{ objectFit: 'cover' }}
                        />
                      ) : isUploaded ? (
                        <video
                          src={src}
                          preload="metadata"
                          muted
                          playsInline
                          className="video-showcase-thumb"
                        >
                          Your browser does not support the video tag.
                        </video>
                      ) : (
                        <div className="video-showcase-thumb video-showcase-thumb--placeholder">
                          <span className="video-showcase-thumb-label">External video</span>
                        </div>
                      )}
                      <div className="video-showcase-play-overlay">
                        <span className="video-showcase-play-icon">▶</span>
                      </div>
                    </div>
                    <div className="video-showcase-card-body">
                      <h3 className="video-showcase-card-title">{video.title}</h3>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {active && (
        <div
          className="video-showcase-modal-overlay"
          onClick={closeModal}
          role="dialog"
          aria-modal="true"
          aria-label="Video player"
        >
          <div className="video-showcase-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="video-showcase-modal-close"
              onClick={closeModal}
              aria-label="Close video"
            >
              ×
            </button>
            {renderModalContent()}
          </div>
        </div>
      )}

      <FooterOne />
    </>
  );
}

export default CategoryVideosPage;
